import React, { FC } from 'react'
import { useQuery } from '@apollo/client'
import { Region } from '@babylon/consumer-api/types'
import { useFormatMessage } from '@babylon/intl'
import {
  SelectField as SelectFieldMedkit,
  Label,
  HelpText,
} from '@babylon/medkit'

import { Heading, Text, Select } from '@babylon/web-platform-ui'
import RegionsQuery from '@/queries/Regions'
import Loading from '@/components/Loading'
import { useLocalisation } from '@/redux/productConfig/selectors'
import messages from './messages'
import { useConfig } from '@/redux/partnerConfig/selectors'
import withField from '../Forms/withField'

interface Props {
  label: string
}

type RegionsResponse = {
  regions: Region[]
}

const SelectField = withField(Select)

const PhoneCountryCodeField: FC<Props> = ({ label }: Props) => {
  const translate = useFormatMessage()
  const { region: regionIsoCode } = useLocalisation()
  const { useDnaV2Design, isAccountCreationV2 } = useConfig()
  const {
    data: { regions } = {},
    loading: regionsLoading,
  } = useQuery<RegionsResponse>(RegionsQuery)

  if (!regions && regionsLoading) {
    return <Loading inline />
  }

  // Move app region to the top
  const regionList = [...(regions || [])].sort((region) =>
    region.iso_code === regionIsoCode ? -1 : 1
  )

  const phoneNumberOptions = regionList.map(
    ({ id, default_phone_country_code: phoneCountryCode, name }) => ({
      key: id!,
      value: phoneCountryCode!,
      label: `${name} ${phoneCountryCode}`,
    })
  )

  const autoSuggestOptions = regionList.map(
    ({ id, default_phone_country_code: phoneCountryCode, name }) => (
      <option key={id!} value={phoneCountryCode!}>
        {phoneCountryCode} {name}
      </option>
    )
  )

  if (useDnaV2Design && isAccountCreationV2) {
    return (
      <>
        <div>
          {/* add padding bottom once styleProps are exposed */}
          <Heading size="2xs" as="h3">
            {translate(messages.preFieldLabelV2)}
          </Heading>
          <Text>{translate(messages.preFieldHelpTextV2)}</Text>
        </div>
        <SelectField name="phone_country_code" label={label}>
          {autoSuggestOptions}
        </SelectField>
      </>
    )
  }

  if (useDnaV2Design) {
    return (
      <>
        <div>
          {/* add padding bottom once styleProps are exposed */}
          <Heading size="2xs" as="h3">
            {translate(messages.preFieldLabel)}
          </Heading>
          <Text>{translate(messages.preFieldHelpText)}</Text>
        </div>
        <SelectField name="phone_country_code" label={label}>
          {autoSuggestOptions}
        </SelectField>
      </>
    )
  }

  return (
    <>
      <Label label={translate(messages.preFieldLabel)} />
      <HelpText>{translate(messages.preFieldHelpText)}</HelpText>
      <SelectFieldMedkit
        name="phone_country_code"
        data-testid="phone_country_code"
        options={phoneNumberOptions}
        label={label}
      />
    </>
  )
}

export default PhoneCountryCodeField
